/**
 * One customer's personal-tenant mode (0175, Phase D).
 *
 * ── The override, not the mode ──────────────────────────────────────────
 *
 * This control writes `tenants.personal_mode_override`, which has three states
 * and whose third is `null` = *inherit the deployment default*. The EFFECTIVE
 * mode is shown beneath, because the override alone cannot answer the question
 * an operator is actually asking: clearing it is a no-op on a deployment whose
 * default already matches, and a Drive appearing or vanishing on one whose does
 * not. Both facts on screen, always — the same reasoning the audit row uses for
 * storing the effective mode beside the column.
 *
 * ── An ORGANIZATION renders, read-only, and that is not decoration ──────
 *
 * `resolveTenantModeForType` short-circuits any non-personal tenant to `'full'`
 * BEFORE reading the column, so the setting is inert on an organization and the
 * write route refuses one with a stable code. Rendering nothing at all would be
 * defensible — but "can I make this organization identity-only?" is a question
 * an operator will have, and a screen that simply omits the control answers it
 * by silence, which is indistinguishable from the control having failed to
 * load. One line that says *no, and here is why* is cheaper than the support
 * conversation.
 *
 * It is deliberately NOT a disabled copy of the real control. A greyed-out
 * three-way picker invites "who do I ask to enable this", when the answer is
 * that the question does not apply to this kind of tenant at all.
 *
 * ── Commits immediately, with a confirmation on the outcome ─────────────
 *
 * Consistent with this console's own rule: a choice among a few fixed values
 * has nothing to mistype, so it commits (the same reasoning the enforcement-mode
 * and plan-authority buttons use), while anything typed stages behind Save.
 *
 * The confirmation fires only when the EFFECTIVE mode moves, so switching
 * between Inherit and Full on a full-default deployment — which changes nothing
 * for the person — does not nag. And like the deployment-wide control, it is
 * asymmetric: taking a Drive away is `danger`, giving one back is `info`.
 * Phrasing both for the worse case teaches operators to click through the
 * warning that mattered.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from '@papan/i18n'
import { useToast, useConfirm } from '@papan/core-ui/ui'
import { type TenantMode } from '@papan/types'
import { budgetApi, type AdminTenantRow } from '../../client/budget'
import { Section } from './bits'

/** The three positions, in the order they are offered. `undefined` is the
 *  inherit position — `null` is a legitimate stored value, so a sentinel that
 *  is itself `null` could not be told apart from "not chosen yet". */
const POSITIONS: { key: 'inherit' | TenantMode; value: TenantMode | null }[] = [
  { key: 'inherit', value: null },
  { key: 'full', value: 'full' },
  { key: 'identity-only', value: 'identity-only' },
]

export function CustomerPersonalMode({
  row, canEdit, onSaved,
}: {
  row: AdminTenantRow
  canEdit: boolean
  onSaved: () => void
}) {
  const { t } = useTranslation('core-ui')
  const toast = useToast()
  const confirm = useConfirm()
  const [saving, setSaving] = useState(false)

  /**
   * The DEPLOYMENT default — what "Inherit" actually resolves to.
   *
   * Self-fetched, like `LandingSetting`, because the customer row does not
   * carry it and the alternative was worse than an extra request: deriving it
   * from `effectiveMode` is impossible once the tenant HAS an override, since
   * the effective mode is then the override's own value. The first draft
   * guessed (`stored === 'identity-only' ? 'full' : …`) and would have offered
   * "give this drive back" on a deployment that defaults to identity-only —
   * confidently, and wrongly, on the one screen that exists to say what will
   * happen.
   *
   * `null` = not known (the read failed, or has not landed). Every consumer
   * below degrades to a statement that does not name the outcome, rather than
   * to an assumption. Safe to attempt from here: every platform role that can
   * open this page holds `settings:read`.
   */
  const [deploymentDefault, setDeploymentDefault] = useState<TenantMode | null>(null)
  useEffect(() => {
    if (row.type !== 'personal') return
    let live = true
    budgetApi.getPlatformSettings()
      .then(r => { if (live) setDeploymentDefault(r.resolved.defaultPersonalTenantMode) })
      .catch(() => { /* stays null — the copy below stops naming the outcome */ })
    return () => { live = false }
  }, [row.type])

  // Optimistic only for the duration of the request: re-seeded from the row on
  // every reload, so a rejected write cannot leave a phantom selection.
  const stored = row.personalModeOverride ?? null
  const [pendingWrite, setPendingWrite] = useState<TenantMode | null | undefined>(undefined)
  useEffect(() => { setPendingWrite(undefined) }, [stored])
  const selected = pendingWrite === undefined ? stored : pendingWrite

  const label = (m: TenantMode) =>
    m === 'identity-only'
      ? t('settings.platform.settings.modeIdentityOnly', { defaultValue: 'Identity-only' })
      : t('settings.platform.settings.modeFull', { defaultValue: 'Full' })

  // ── The organization case ────────────────────────────────────────────
  if (row.type !== 'personal') {
    return (
      <Section
        title={t('settings.platform.customer.modeTitle', { defaultValue: 'Personal tenant mode' })}
      >
        <div className="themed-bg-secondary rounded-lg p-3">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="themed-text text-sm font-medium">{label('full')}</span>
            <span className="themed-text-muted text-xs">
              {t('settings.platform.customer.modeOrgOnly', {
                defaultValue:
                  'Organizations are always full workspaces. This setting applies to personal tenants — the drive each person has of their own.',
              })}
            </span>
          </div>
        </div>
      </Section>
    )
  }

  // ── The personal case ────────────────────────────────────────────────
  //
  // `effectiveMode` / `modeSource` come from the server, resolved by the same
  // function the ENFORCEMENT reader uses, so this screen cannot display a mode
  // the gate would disagree with. Never re-derived here from the override plus
  // a guess at the deployment default: that is the second implementation this
  // feature spent C1 avoiding.
  const effective: TenantMode = row.effectiveMode ?? 'full'
  const source = row.modeSource ?? 'default'

  const choose = async (next: TenantMode | null) => {
    if (next === stored) return

    // What the PERSON would get — which is what the dialog has to be about.
    // Clicking "Inherit" resolves through the deployment default, so the
    // outcome is not readable from this control alone; `null` here means we
    // could not read it, and the copy below stops claiming an outcome rather
    // than inventing one.
    const wouldBe: TenantMode | null = next ?? deploymentDefault

    // `null` (unknown) confirms too. Skipping the dialog because we cannot
    // predict the result would be the permissive direction on the one action
    // that removes a person's drive.
    if (wouldBe !== effective) {
      const restricting = wouldBe === 'identity-only'
      const unknown = wouldBe === null
      const ok = await confirm({
        title: unknown
          ? t('settings.platform.customer.modeConfirmClearTitle', {
              defaultValue: 'Follow the deployment default?',
            })
          : restricting
            ? t('settings.platform.customer.modeConfirmRestrictTitle', {
                defaultValue: 'Take away this drive?',
              })
            : t('settings.platform.customer.modeConfirmRelaxTitle', {
                defaultValue: 'Give this drive back?',
              }),
        message: unknown
          ? t('settings.platform.customer.modeConfirmClear', {
              defaultValue:
                '{{name}} will follow this deployment\'s default from now on, and their drive changes with it. Nothing is deleted — their files stay exactly where they are.',
              name: row.name,
            })
          : restricting
            ? t('settings.platform.customer.modeConfirmRestrict', {
                defaultValue:
                  '{{name}} loses their personal drive immediately. They keep their account, their organizations, and everything shared with them. Nothing is deleted — their files stay exactly where they are and come back if you switch this again.',
                name: row.name,
              })
            : t('settings.platform.customer.modeConfirmRelax', {
                defaultValue:
                  '{{name}} gets their personal drive back immediately, with their existing files intact.',
                name: row.name,
              }),
        confirmText: unknown
          ? t('common:continue', { defaultValue: 'Continue' })
          : restricting
            ? t('settings.platform.customer.modeConfirmRestrictOk', { defaultValue: 'Remove drive' })
            : t('settings.platform.customer.modeConfirmRelaxOk', { defaultValue: 'Restore drive' }),
        type: restricting ? 'danger' : unknown ? 'warning' : 'info',
      })
      if (!ok) return
    }

    setPendingWrite(next)
    setSaving(true)
    try {
      await budgetApi.setTenantMode(row.id, next)
      toast.success(t('settings.platform.customer.modeSaved', { defaultValue: 'Mode updated.' }))
      onSaved()
    } catch (e: any) {
      setPendingWrite(undefined)
      toast.error(e?.message || 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Section
      title={t('settings.platform.customer.modeTitle', { defaultValue: 'Personal tenant mode' })}
      desc={t('settings.platform.customer.modeDesc', {
        defaultValue:
          'Whether this person has a drive of their own. They keep their account and their organizations either way — this is not a suspension, and nothing is deleted when it is switched off.',
      })}
    >
      <div className="themed-bg-secondary rounded-lg p-3">
        <div
          role="radiogroup"
          aria-label={t('settings.platform.customer.modeTitle', { defaultValue: 'Personal tenant mode' })}
          className="flex flex-wrap gap-1.5"
        >
          {POSITIONS.map(({ key, value }) => {
            const active = selected === value
            return (
              <button
                key={key}
                role="radio"
                aria-checked={active}
                onClick={() => void choose(value)}
                disabled={saving || !canEdit}
                data-testid={`customer-mode-${key}`}
                className={`px-2.5 py-1 text-xs rounded transition-colors disabled:opacity-50 ${
                  active
                    ? 'bg-[color:var(--bill-tint)] text-[color:var(--bill-primary)] ring-1 ring-[color:var(--bill-ring)]'
                    : 'themed-btn-secondary'
                }`}
              >
                {key === 'inherit'
                  // Names what inheriting RESOLVES to once we know it. "Inherit"
                  // alone is a question, and this is the position an operator
                  // picks when they want the deployment's answer — so it should
                  // say what that answer currently is.
                  ? deploymentDefault
                    ? t('settings.platform.customer.modeInheritNamed', {
                        defaultValue: 'Inherit ({{mode}})', mode: label(deploymentDefault),
                      })
                    : t('settings.platform.customer.modeInherit', { defaultValue: 'Inherit' })
                  : label(key)}
              </button>
            )
          })}
        </div>

        {/* The resolved answer, and which link produced it. Without the source
            an operator reads "Identity-only" beside an untouched control and
            cannot tell a deployment default from a decision someone made about
            this person. */}
        <div className="mt-2.5 text-[11px] themed-text-muted">
          {t('settings.platform.customer.modeEffective', {
            defaultValue: 'In force: {{mode}}',
            mode: label(effective),
          })}
          {' — '}
          {/* `TenantModeSource` is 'tenant' | 'platform' | 'default' — NOT the
              platform-wide control's 'setting' | 'default'. The two unions are
              deliberately different (there is no tenant in play on a
              deployment-wide screen), and writing one's member into the other
              is a silent no-match that renders the wrong sentence. Typecheck
              caught exactly that here. */}
          {source === 'tenant'
            ? t('settings.platform.customer.modeSrcTenant', { defaultValue: 'set here, for this customer.' })
            : source === 'platform'
              ? t('settings.platform.customer.modeSrcSetting', {
                  defaultValue: "inherited from this deployment's default.",
                })
              : t('settings.platform.customer.modeSrcDefault', {
                  defaultValue: 'the shipped default — nothing has been configured.',
                })}
        </div>
      </div>
    </Section>
  )
}
